import { useEffect, useRef } from 'react';
import { useStytchMemberSession, useStytchMember } from '@stytch/react/b2b';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { setCurrentStytchJWT } from '../../services/api';
import { stytch } from './StytchProvider';

/**
 * StytchSessionSync - Syncs Stytch session with backend auth state
 * 
 * CRITICAL: This component must NOT cause infinite loops or page refreshes
 * - Only fetches profile ONCE when Stytch session exists but auth is not set
 * - Does NOT signout on 401 errors (prevents infinite loop)
 * - Uses refs to prevent multiple concurrent fetches
 * - NOT wrapped in observer() to prevent re-render loops
 */
export const StytchSessionSync: React.FC = () => {
  const { session } = useStytchMemberSession();
  const { member } = useStytchMember(); // Get full member object from Stytch
  const appStore = useAppStore();
  const hasSyncedRef = useRef(false);
  const hasClearedRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Monitor Stytch session and update JWT whenever it changes (including auto-refresh)
  // Poll every 30 seconds because Stytch refreshes JWT in background without changing session object
  useEffect(() => {
    if (!session) return;
    
    const updateJWT = () => {
      try {
        const tokens = stytch.session.getTokens();
        const extractedJWT = tokens?.session_jwt || tokens?.session_token || null;
        
        if (extractedJWT) {
          // Always update the global JWT (Stytch auto-refreshes in the background)
          setCurrentStytchJWT(extractedJWT);
          console.log('🔄 [StytchSessionSync] JWT updated/refreshed');
        }
      } catch (error) {
        console.error('❌ [StytchSessionSync] Error updating JWT:', error);
      }
    };
    
    // Update immediately
    updateJWT();
    
    // Poll every 30 seconds to catch Stytch's background JWT refreshes
    // Stytch JWTs last 5 minutes, so checking every 30 seconds ensures we never use expired tokens
    const interval = setInterval(updateJWT, 30000);
    
    return () => clearInterval(interval);
  }, [session]); // Re-run whenever session object changes (includes refresh)
  
  useEffect(() => {
    // On public routes, store might be null - that's OK, nothing to sync
    if (!appStore) {
      console.log('🎯 [StytchSessionSync] No store on public route, skipping sync');
      return;
    }
    
    const auth = appStore.auth;
    
    console.log('🔄 [StytchSessionSync] useEffect fired, session:', !!session, 'auth:', auth.isAuthenticated, 'hasSynced:', hasSyncedRef.current);
    
    // DEBUG: Log session structure to see what we have
    if (session) {
      console.log('🔍 [StytchSessionSync] Session structure:', {
        hasMember: !!session.member,
        hasOrganization: !!session.organization,
        memberId: session.member_id,
        organizationId: session.organization_id,
        sessionKeys: Object.keys(session)
      });
    }
    
    // DEBUG: Log member data
    if (member) {
      console.log('🔍 [StytchSessionSync] Member data:', {
        email: member.email_address,
        name: member.name,
        memberId: member.member_id,
        trustedMetadata: member.trusted_metadata,
      });
    }
    
    // Use Stytch session as the SOLE source of truth
    // Only sync once when we have a Stytch session but no auth state
    // Check for member_id instead of member object (Stytch session structure varies)
    if (session && session.member_id && session.organization_id && member && !auth.isAuthenticated && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      
      // Use member from useStytchMember hook (has full data)
      const org = session.organization || session;
      
      console.log('✅ [StytchSessionSync] Syncing from Stytch session (SOLE SOURCE OF TRUTH)');
      console.log('   Member ID:', session.member_id);
      console.log('   Member Email:', member.email_address);
      console.log('   Member Name:', member.name);
      console.log('   Stytch Organization ID:', session.organization_id);
      console.log('   Session ID:', session.session_id);
      
      // CRITICAL: Extract and store the JWT for API calls!
      // The session object from the hook doesn't include session_jwt directly
      // We need to get it from the Stytch client instance
      let extractedJWT: string | null = null;
      
      try {
        const tokens = stytch.session.getTokens();
        console.log('🔍 [StytchSessionSync] Got tokens from Stytch client:', {
          hasSessionToken: !!tokens?.session_token,
          hasSessionJWT: !!tokens?.session_jwt,
          keys: tokens ? Object.keys(tokens) : []
        });
        
        extractedJWT = tokens?.session_jwt || tokens?.session_token || null;
        
        if (extractedJWT) {
          console.log('🔑 [StytchSessionSync] Found session JWT from Stytch client!');
          console.log('   JWT (first 50 chars):', extractedJWT.substring(0, 50) + '...');
          setCurrentStytchJWT(extractedJWT);
        } else {
          console.warn('⚠️ [StytchSessionSync] No JWT available from Stytch client!');
          console.warn('   Tokens object:', tokens);
          console.warn('   This will cause API calls to fail with 401!');
        }
      } catch (error) {
        console.error('❌ [StytchSessionSync] Error getting tokens from Stytch client:', error);
      }
      
      // Wrap async code in an async function
      const syncSession = async () => {
        // Fetch the PostgreSQL organization ID from backend using Stytch org ID
        // CRITICAL: Use axios (not fetch) so the interceptor adds the JWT to Authorization header!
        try {
          const { default: api } = await import('../../services/api');
          
          console.log(`📡 [StytchSessionSync] Fetching org from backend: /organizations/by-stytch-id/${session.organization_id}`);
          
          const orgResponse = await api.get(`/organizations/by-stytch-id/${session.organization_id}`);
          
          const orgData = orgResponse.data;
          console.log('✅ [StytchSessionSync] Got PostgreSQL org from backend:', orgData);
          
          // Extract name from member object
          let displayName = 'User';
          if (typeof member.name === 'string') {
            displayName = member.name;
          } else if (member.name && (member.name.first_name || member.name.last_name)) {
            displayName = `${member.name.first_name || ''} ${member.name.last_name || ''}`.trim();
          } else if (member.email_address) {
            displayName = member.email_address.split('@')[0];
          }
          
          // Extract role from trusted_metadata (set by our fix-stytch-member-rbac script)
          const userRole = member.trusted_metadata?.role || 'member';
          
          console.log('✅ [StytchSessionSync] User data:', {
            displayName,
            email: member.email_address,
            role: userRole,
            trustedMetadata: member.trusted_metadata
          });
          
          // Set auth using proper PostgreSQL IDs and member data
          auth.setAuth({
            user: {
              id: session.member_id,
              email: member.email_address || 'unknown@example.com',
              name: displayName,
              organizationId: orgData.id, // PostgreSQL UUID
              role: userRole, // From Stytch trusted_metadata
              stytchMemberId: session.member_id,
            },
            organization: {
              id: orgData.id, // PostgreSQL UUID - needed for API calls!
              slug: orgData.slug || org.organization_slug || org.slug || 'unknown-org',
              name: orgData.name || org.organization_name || org.name || 'Unknown Organization',
              stytchOrganizationId: session.organization_id, // Keep Stytch ID for reference
            },
            token: 'stytch-session', // Mark that we're using Stytch session
          });
          
          console.log('✅ [StytchSessionSync] Auth state synced with PostgreSQL org ID!');
          console.log('   PostgreSQL Org ID:', orgData.id);
          console.log('   Stytch Org ID:', session.organization_id);
        } catch (error: any) {
          console.error('❌ [StytchSessionSync] Failed to fetch org from backend:', error);
          
          // Increment retry counter
          retryCountRef.current += 1;
          
          // If we get 401, check if it's a JWT issue
          if (error?.response?.status === 401 || error?.status === 401) {
            console.error(`🚫 [StytchSessionSync] Session rejected by backend (401) - Attempt ${retryCountRef.current}/${maxRetries}`);
            
            // Check if we have a JWT to send
            if (!extractedJWT) {
              console.error('   Problem: No JWT available to send to backend!');
              console.error('   This means stytch.session.getTokens() returned nothing');
            }
            
            // After max retries, give up and redirect
            if (retryCountRef.current >= maxRetries) {
              console.error('🔴 [StytchSessionSync] Max retries reached, clearing session and forcing fresh login');
              sessionStorage.setItem('stytch_sync_failed', 'true');
              hasSyncedRef.current = false; // Reset so it doesn't try again
              // Don't redirect here - let the AuthenticatedRoute timeout handle it
              // This prevents double redirects
            } else {
              // Retry after a delay
              console.log(`⏳ [StytchSessionSync] Will retry in ${retryCountRef.current} seconds...`);
              setTimeout(() => {
                hasSyncedRef.current = false; // Reset to allow retry
              }, retryCountRef.current * 1000);
            }
          }
        }
      };
      
      // Call the async function
      syncSession();
    } else if (!session && auth.isAuthenticated && !hasClearedRef.current) {
      // If Stytch session is gone but we still have auth, clear it (once)
      hasClearedRef.current = true;
      console.log('🔄 [StytchSessionSync] No Stytch session, clearing auth state');
      setCurrentStytchJWT(null); // Clear the global JWT
      auth.clearAuth();
    }
    
    // Reset cleared ref when session comes back
    if (session && hasClearedRef.current) {
      hasClearedRef.current = false;
    }
  }, [session, member, appStore]); // Depend on session, member, and store reference

  return null;
};

export default StytchSessionSync;
